import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useQueryClient } from "@tanstack/react-query";
import { useSession } from "@/services/auth";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  InputOtp,
  Spinner,
} from "@heroui/react";
import { Mail, LogOut } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * Pre-link verification screen
 * Shown when a user signs up with Clerk but their email matches an existing customer.
 * They must verify ownership of the email to link accounts.
 */
export default function PreLinkVerificationScreen() {
  const { token, logout, refetchCustomer } = useSession();
  const queryClient = useQueryClient();
  const { t } = useTranslation("app");

  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [resendCountdown, setResendCountdown] = useState(0);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isCountingDown = resendCountdown > 0;
  useEffect(() => {
    if (!isCountingDown) return;
    countdownRef.current = setInterval(() => {
      setResendCountdown((prev) => prev - 1);
    }, 1000);
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [isCountingDown]);

  const handleSendCode = async () => {
    setSending(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await axios.post<{
        success: boolean;
        alreadyVerified?: boolean;
      }>(
        `${import.meta.env.VITE_API_URL}/api/v2/verification/pre-link/send`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.alreadyVerified) {
        setSuccessMessage(t("preLinkVerification.linkedRedirecting"));
        void queryClient.invalidateQueries({ queryKey: ["customer", "me"] });
        refetchCustomer();
      } else {
        setCodeSent(true);
        setSuccessMessage(t("preLinkVerification.codeSent"));
        setResendCountdown(60);
      }
    } catch (err: unknown) {
      const data = axios.isAxiosError<{
        error?: string;
        cooldownRemaining?: number;
      }>(err)
        ? err.response?.data
        : undefined;
      if (data?.cooldownRemaining) {
        setResendCountdown(data.cooldownRemaining);
        setError(t("preLinkVerification.waitBeforeRequest"));
      } else {
        setError(data?.error ?? t("preLinkVerification.sendFailed"));
      }
    } finally {
      setSending(false);
    }
  };

  const handleVerifyCode = async () => {
    if (code.length !== 6) return;

    setVerifying(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await axios.post(
        `${import.meta.env.VITE_API_URL}/api/v2/verification/pre-link/verify`,
        { code },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccessMessage(t("preLinkVerification.verifiedRedirecting"));
      void queryClient.invalidateQueries({ queryKey: ["customer", "me"] });
      refetchCustomer();
    } catch (err: unknown) {
      const data = axios.isAxiosError<{ error?: string }>(err)
        ? err.response?.data
        : undefined;
      setError(data?.error ?? t("preLinkVerification.invalidCode"));
      setCode("");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-default-50 p-4">
      <Card className="w-full max-w-md" shadow="lg">
        <CardHeader className="flex-col items-center pb-2 pt-6">
          <div className="mb-4 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Mail className="h-6 w-6 text-primary" />
          </div>
          <h2 className="text-2xl font-semibold">
            {t("preLinkVerification.title")}
          </h2>
        </CardHeader>
        <CardBody className="space-y-4">
          <p className="text-center text-default-500">
            {t("preLinkVerification.body")}
          </p>

          {error && (
            <div className="rounded-medium bg-danger-50 p-3 text-small text-danger text-center">
              {error}
            </div>
          )}

          {successMessage && (
            <div className="rounded-medium bg-success-50 p-3 text-small text-success text-center">
              {successMessage}
            </div>
          )}

          {!codeSent ? (
            <Button
              color="primary"
              fullWidth
              onPress={() => void handleSendCode()}
              isDisabled={sending}
              startContent={
                sending ? <Spinner size="sm" color="white" /> : null
              }
            >
              {sending
                ? t("preLinkVerification.sending")
                : t("preLinkVerification.sendCode")}
            </Button>
          ) : (
            <div className="space-y-4">
              <p className="text-small text-default-500 text-center">
                {t("preLinkVerification.enterCode")}
              </p>

              <div className="flex justify-center">
                <InputOtp length={6} value={code} onValueChange={setCode} />
              </div>

              <Button
                color="primary"
                fullWidth
                onPress={() => void handleVerifyCode()}
                isDisabled={verifying || code.length !== 6}
                startContent={
                  verifying ? <Spinner size="sm" color="white" /> : null
                }
              >
                {verifying
                  ? t("preLinkVerification.verifying")
                  : t("preLinkVerification.verifyCode")}
              </Button>

              <Button
                variant="light"
                size="sm"
                fullWidth
                onPress={() => void handleSendCode()}
                isDisabled={sending || resendCountdown > 0}
              >
                {sending
                  ? t("preLinkVerification.sending")
                  : resendCountdown > 0
                    ? t("preLinkVerification.resendCodeWithCountdown", {
                        seconds: resendCountdown,
                      })
                    : t("preLinkVerification.resendCode")}
              </Button>
            </div>
          )}

          <div className="pt-2 border-t border-divider">
            <Button
              variant="light"
              size="sm"
              fullWidth
              onPress={logout}
              className="text-default-500"
              startContent={<LogOut className="h-4 w-4" />}
            >
              {t("preLinkVerification.signOut")}
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
